import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, message, Modal, Input, Form } from 'antd';
import {
  DashboardOutlined, CarOutlined, SafetyOutlined, DollarOutlined,
  BellOutlined, UserOutlined, LogoutOutlined, MenuFoldOutlined, KeyOutlined,
  MenuUnfoldOutlined, TeamOutlined, BankOutlined, InboxOutlined,
  AuditOutlined, HistoryOutlined, ShopOutlined, FieldTimeOutlined, FolderOpenOutlined, SettingOutlined, SwapOutlined, ProfileOutlined, VideoCameraOutlined, WalletOutlined, SafetyCertificateOutlined, FileTextOutlined, BarChartOutlined
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { confirmLeave } from '../utils/leaveGuard';
import { getUserLevel, hasPermission, PERMS } from '../utils/permissions';
import { useTabs } from '../context/TabContext';
import TabBar from './TabBar';
import GlobalSearch from './GlobalSearch';
import SiteFooter from './SiteFooter';
import { APP_VERSION } from '../version';

const { Header, Sider, Content } = Layout;

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(getUserLevel());
  const navigate = useNavigate();
  const location = useLocation();
  const { openTab, resetTabs } = useTabs();
  const username = localStorage.getItem('username') || '';

  // 组件挂载时刷新用户信息
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/users/me').then(res => {
        const level = res.data.user_level || 3;
        setUserLevel(level);
        localStorage.setItem('user_level', String(level));
        const perms = res.data.permissions || [];
        localStorage.setItem('permissions', JSON.stringify(perms));
        localStorage.setItem('user_city', res.data.city || '');
        localStorage.setItem('user_company', res.data.company || '');
      }).catch(() => {});
    }
  }, []);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await api.post('/auth/logout');
    } catch (e) { /* ignore */ }
    resetTabs();
    localStorage.clear();
    message.success('已退出登录');
    navigate('/login');
  };

  // 所有菜单全部走 hasPermission 细粒度控制
  const insuranceItems = [];
  if (hasPermission(PERMS.INSURANCE_VIEW)) {
    insuranceItems.push({ key: '/insurance', icon: <SafetyOutlined />, label: '保险记录' });
  }
  if (hasPermission(PERMS.INSURANCE_COMPANY_VIEW)) {
    insuranceItems.push({ key: '/insurance/companies', icon: <BankOutlined />, label: '保险公司' });
  }
  // 财务管理（2026-10-31 邢先生：从「保险管理」子菜单提升为独立一级菜单）
  //   原：保险管理 → 财务管理（子菜单）
  //   现：保险管理 | 审验管理 | 财务管理（平级）
  const financeItems = [];
  if (hasPermission(PERMS.SETTLEMENT_VIEW) || hasPermission(PERMS.REBATE_VIEW)) {
    financeItems.push({ key: '/rebates', icon: <DollarOutlined />, label: '财务管理' });
  }

  const baseItems = [
    { key: '/', icon: <DashboardOutlined />, label: '首页' },
  ];
  if (hasPermission(PERMS.VEHICLE_VIEW)) {
    baseItems.push({ key: '/vehicles', icon: <CarOutlined />, label: '档案管理' });
    baseItems.push({ key: '/drivers', icon: <TeamOutlined />, label: '司机管理' });
  }
  if (insuranceItems.length > 0) {
    baseItems.push({ key: '/insurance', icon: <SafetyOutlined />, label: '保险管理', children: insuranceItems });
  }
  if (hasPermission(PERMS.INSPECTION_VIEW)) {
    baseItems.push({ key: '/inspections', icon: <BellOutlined />, label: '审验管理' });
  }
  // 2026-10-31：财务管理放在「保险管理」下边、与「审验管理」平级
  baseItems.push(...financeItems);
  // 2026-11-03 员工工作量（仅老板可见）
  if (hasPermission(PERMS.USER_MANAGE)) {
    baseItems.push({ key: '/staff-workload', icon: <BarChartOutlined />, label: '员工工作量' });
  }
  // 待办提醒：单页（到期待办）
  if (hasPermission(PERMS.INSURANCE_VIEW)) {
    baseItems.push({ key: '/todos', icon: <BellOutlined />, label: '待办提醒' });
  }
  if (hasPermission(PERMS.SUBMISSION_VIEW)) {
    baseItems.push({ key: '/submissions', icon: <InboxOutlined />, label: '档案收集' });
  }
  // 司机培训
  // ⭐ 2026-10-23 修正：原来整组菜单只看 driver:view + 潍坊，但下属页面的接口要 training:view，
  // 结果「菜单看得见、点进去 403」。改为按子项各自权限动态组装，一项都没有就不出菜单。
  // 🚨 2026-10-28 邢先生拍板（方案 A）：**去掉「潍坊专用」硬限制**，改为纯按权限显示。
  //   原因：邢建（super_admin，city 为空）权限全有，却因为城市判断看不到「课程管理/课程订单」。
  //   既然权限系统已精确控制（training:view / training:edit / training:refund），
  //   城市硬编码是多余的：有权限的自然看到，没权限的看不到。
  {
    const trainItems = [];
    if (hasPermission(PERMS.TRAINING_VIEW) || hasPermission(PERMS.DRIVER_VIEW)) {
      trainItems.push({ key: '/driver-training', icon: <FieldTimeOutlined />, label: '培训进度' });
    }
    if (hasPermission(PERMS.TRAINING_MANAGE)) {
      trainItems.push({ key: '/course-manage', icon: <VideoCameraOutlined />, label: '课程管理' });
    }
    if (hasPermission(PERMS.TRAINING_VIEW)) {
      trainItems.push({ key: '/course-orders', icon: <WalletOutlined />, label: '课程订单' });
      trainItems.push({ key: '/training-verification', icon: <SafetyCertificateOutlined />, label: '学习认证' });
    }
    if (trainItems.length) {
      baseItems.push({ key: '/driver-training', icon: <FieldTimeOutlined />, label: '司机培训', children: trainItems });
    }
  }
  // 司机换车（潍坊专用）
  if (hasPermission(PERMS.VEHICLE_EDIT) && (localStorage.getItem('user_city') || '潍坊') === '潍坊') {
    baseItems.push({ key: '/driver-transfer', icon: <SwapOutlined />, label: '司机换车' });
  }

  // 审核中心：把各类审核聚合到同一页面（一张列表，类型列区分）
  if (hasPermission(PERMS.VEHICLE_DELETE) || hasPermission(PERMS.DRIVER_DELETE) || hasPermission(PERMS.VEHICLE_VIEW)) {
    baseItems.push({ key: '/audit', icon: <AuditOutlined />, label: '审核中心' });
  }
  // ⭐ 2026-10-25：我的申请——提交了变更/删除申请后，要能查到走到哪了
  baseItems.push({ key: '/my-requests', icon: <FileTextOutlined />, label: '我的申请' });

  const adminItems = [];
  if (hasPermission(PERMS.COMPANY_VIEW)) {
    adminItems.push({ key: '/companies', icon: <BankOutlined />, label: '公司管理' });
  }
  if (hasPermission(PERMS.USER_MANAGE)) {
    adminItems.push({ key: '/users', icon: <TeamOutlined />, label: '用户管理' });
  }
  if (hasPermission(PERMS.VEHICLE_DELETE) || hasPermission(PERMS.DRIVER_DELETE)) {
    adminItems.push({ key: '/recycle-bin', icon: <ShopOutlined />, label: '回收站' });
  }
  if (hasPermission(PERMS.USER_MANAGE)) {
    adminItems.push({ key: '/archive-sync', icon: <FolderOpenOutlined />, label: '归集同步' });
    adminItems.push({ key: '/material-config', icon: <ProfileOutlined />, label: '材料需求配置' });
    adminItems.push({ key: '/logs', icon: <HistoryOutlined />, label: '操作日志' });
  }

  const menuItems = [...baseItems];
  if (adminItems.length > 0) {
    menuItems.push({
      key: '/system', icon: <SettingOutlined />, label: '系统管理', children: adminItems,
    });
  }

  // 菜单/路由 => 标签标题映射（全局标签栏用）
  const ROUTE_LABELS = {
    '/': '首页',
    '/vehicles': '档案管理',
    '/drivers': '司机管理',
    '/vehicles/new': '新建档案',
    '/insurance': '保险记录',
    '/insurance/companies': '保险公司',
    '/insurance/settlement': '财务对账',
    '/rebates': '财务管理',
    '/staff-workload': '员工工作量',
    '/rebates/stats': '返利报表',
    '/inspections': '审验管理',
    '/todos': '待办提醒',
    '/reminder-tasks': '提醒任务',
    '/submissions': '档案收集',
    '/driver-training': '司机培训',
    '/course-manage': '课程管理',
    '/course-orders': '课程订单',
    '/training-verification': '学习认证',
    '/audit': '审核中心',
    '/delete-requests': '删除审核',
    '/recycle-bin': '回收站',
    '/companies': '公司管理',
    '/users': '用户管理',
    '/logs': '操作日志',
    '/archive-sync': '归集同步',
    '/material-config': '材料需求配置',
    '/plate-change': '车牌变更审核',
    '/my-requests': '我的申请',
  };
  const labelForPath = (pathname) => {
    if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
    if (/^\/vehicles\/\d+/.test(pathname)) return '档案详情';
    if (/^\/submissions\/\d+/.test(pathname)) return '提审详情';
    return pathname;
  };

  // 当前路由变化时，自动登记/激活标签（保证直接进入、浏览器前进后退也能入标签）
  useEffect(() => {
    const pathname = location.pathname;
    if (!pathname || pathname === '/login') return;
    openTab({ key: pathname, label: labelForPath(pathname), path: pathname, closable: true });
  }, [location.pathname]);

  const dropdownItems = [
    {
      key: 'info',
      label: (
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontWeight: 500 }}>{username}</div>
          <div style={{ fontSize: 12, color: '#999' }}>
            {userLevel === 1 ? '超级管理员' : userLevel === 2 ? '管理员' : userLevel === 3 ? '公司管理员' : '员工'}
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    { key: 'changePwd', icon: <KeyOutlined />, label: '修改密码', onClick: () => setChangePwdOpen(true) },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout, danger: true },
  ];

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="dark"
        style={{ position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 301, height: '100vh', overflow: 'hidden' }}>
        <div style={{ height: 64, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: collapsed ? 16 : 20, fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {collapsed ? '睿安' : '睿安车辆管理'}
        </div>
        <div style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }} className="sidebar-menu-scroll">
          <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]} defaultOpenKeys={[]}
            items={menuItems} onClick={({ key }) => { if (!key.includes('/')) return; if (key === location.pathname) return; if (!confirmLeave()) return; navigate(key); }} />
        </div>
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s', height: '100vh', overflow: 'hidden' }}>
        <Header style={{ padding: '0 24px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', position: 'relative', flex: '0 0 auto', zIndex: 300 }}>
          <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <GlobalSearch />
            <span title="页面版本号（用于确认是否刷到最新版）" style={{ fontSize: 12, color: '#999', background: '#f5f5f5', padding: '2px 8px', borderRadius: 10 }}>
              v{APP_VERSION}
            </span>
            <Dropdown menu={{ items: dropdownItems }} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {username}
              </Button>
            </Dropdown>
          </div>
        </Header>
        <TabBar />
        <Content id="main-scroll-container" style={{ margin: 0, padding: 16, minHeight: 280, flex: '1 1 auto', overflow: 'auto' }}>
          <Outlet />
        </Content>
        {/* 网站备案号（工信部要求） */}
        <SiteFooter light />
      </Layout>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={changePwdOpen}
        onCancel={() => { setChangePwdOpen(false); pwdForm.resetFields(); }}
        onOk={async () => {
          try {
            const values = await pwdForm.validateFields();
            if (values.newPassword !== values.confirmPassword) {
              message.error('两次输入的新密码不一致');
              return;
            }
            const token = localStorage.getItem('token');
            const res = await fetch('/api/auth/change-password', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
              },
              body: JSON.stringify({
                old_password: values.oldPassword,
                new_password: values.newPassword
              })
            });
            if (!res.ok) {
              const err = await res.json();
              throw new Error(err.detail || '修改失败');
            }
            message.success('密码修改成功');
            setChangePwdOpen(false);
            pwdForm.resetFields();
          } catch (err) {
            message.error(err.message);
          }
        }}
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item name="oldPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码至少6位' }
          ]}>
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" rules={[
            { required: true, message: '请确认新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error('两次输入的密码不一致'));
              }
            })
          ]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
